/**
 * `/recruiting/inbox` — the Inbox screen, WP-W13 (Phase 4c SPA, consuming the
 * Task 23 inbox admin API). `web/shapes/inbox.md` is the agreed shape, written
 * before this file.
 *
 * The recruiter-facing surface for the inbound-email inbox: the ORIGINAL candidate
 * email that started a conversation, and its CV. It is the human-in-loop context
 * for the AI reply-draft — that draft lives on `OutreachScreen` as a
 * `pending_approval` message; this screen is the source side of that review, and it
 * writes nothing at all. Every request here is a `GET`.
 *
 * ── Two permissions, one screen ─────────────────────────────────────────────
 *
 * The menu item's `requires` is `recruiting.view_candidates` — the floor the route
 * guard checks, and what the list and detail reads need. Reading an attachment's
 * **bytes** needs the stronger `recruiting.view_candidate_documents`, exactly as
 * `openapi.yaml` documents: an inbound attachment may be a candidate's CV, so it is
 * a sensitive read of PII gated on a mandatory `X-Access-Reason`. This screen never
 * checks which code a session holds before offering the button — the server runs
 * that check on every call, and a refusal renders in place through `ApiErrorNote`,
 * exactly the "menu is not a security boundary" rule `nav/menu.ts` states.
 *
 * ── The one hard rule: a rejected attachment is never a download link ────────
 *
 * `InboxAttachment.stored` decides whether the Download button exists for a row at
 * all. `stored: false` means the Phase 4a parser refused the attachment or object
 * storage was unavailable when it arrived; the server's own download endpoint
 * answers `409 WORKFORCE_CONFLICT` for one, "never a broken link" — and this screen
 * keeps that promise on the client side too, by never rendering the button that
 * would produce it. The `rejected_reason` is shown in its place, in words.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';

import { useI18n, type Translate } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import { useSession } from '../../auth/SessionProvider';
import type {
  InboxAttachment,
  InboxMessageDetail,
  InboxMessageListResponse,
  InboxMessageSummary,
  InboxParseStatus,
} from '../../api/inbox';
import { INBOX_PARSE_STATUSES, inboxMessagePath, inboxMessagesPath } from '../../api/inbox';
import type { RecruitingCandidateListResponse } from '../../api/recruiting';
import { recruitingCandidatesPath } from '../../api/recruiting';
import { useApiQuery } from '../../api/useApiQuery';
import {
  deliverBlobDownload,
  downloadInboxAttachment,
  PARSE_STATUS_LABEL,
  PARSE_STATUS_SEVERITY,
  type DeliverBlob,
  type DownloadState,
} from '../../recruiting/inbox';
import {
  isHeaderSafe,
  isUsableReason,
  MAX_ACCESS_REASON,
  MIN_ACCESS_REASON,
} from '../../access/reason';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { PartialData } from '../../states/PartialData';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';
import { SEVERITY_ICON } from '../../states/icons';
import { ScreenHeading } from '../../shell/ScreenHeading';
import type { PickerOption } from './CreateApplicationForm';

const PAGE_LIMIT = 50;
const DEFAULT_TIMEZONE = 'Asia/Ho_Chi_Minh';
const BYTES_PER_KB = 1024;

export interface InboxScreenProps {
  /**
   * How a fetched attachment is handed to the operator. Injected so a test can
   * drive the download without `URL.createObjectURL`, which jsdom does not
   * implement; production uses the real object-URL-and-anchor delivery.
   */
  readonly deliverAttachment?: DeliverBlob;
}

export function InboxScreen({
  deliverAttachment = deliverBlobDownload,
}: InboxScreenProps = {}): ReactNode {
  const { t, locale } = useI18n();
  const { user, client } = useSession();
  const timezone = user?.timezone ?? DEFAULT_TIMEZONE;

  const [statusFilter, setStatusFilter] = useState<'' | InboxParseStatus>('');
  const [candidateFilter, setCandidateFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [downloadState, setDownloadState] = useState<DownloadState>({ status: 'idle' });

  /* ── Candidate enrichment — the same permission as this screen's own floor ───── */
  const candidates = useApiQuery<RecruitingCandidateListResponse>(
    recruitingCandidatesPath({ limit: PAGE_LIMIT }),
  );
  const candidateName = useMemo(() => {
    const map = new Map<string, string>();
    for (const candidate of candidates.data?.data ?? []) map.set(candidate.id, candidate.full_name);
    return map;
  }, [candidates.data]);
  const candidateOptions: readonly PickerOption[] = useMemo(
    () => (candidates.data?.data ?? []).map((c) => ({ id: c.id, label: c.full_name })),
    [candidates.data],
  );

  /* ── The message list — filtered by status and/or candidate ──────────────────── */
  const listPath = inboxMessagesPath({
    limit: PAGE_LIMIT,
    candidateId: candidateFilter === '' ? undefined : candidateFilter,
    status: statusFilter === '' ? undefined : statusFilter,
  });
  const list = useApiQuery<InboxMessageListResponse>(listPath);
  const rows: readonly InboxMessageSummary[] = list.data?.data ?? [];
  const hasMore = list.data?.page.has_more ?? false;

  /* ── The open message's detail ────────────────────────────────────────────────── */
  const detailPath = selectedId === null ? null : inboxMessagePath(selectedId);
  const detail = useApiQuery<InboxMessageDetail>(detailPath);
  const current = detail.data;

  const openMessage = (id: string): void => {
    setSelectedId(id);
    setReason('');
    setDownloadState({ status: 'idle' });
  };

  const reasonUsable = isUsableReason(reason);
  const reasonUnencodable = reason.trim() !== '' && !isHeaderSafe(reason.trim());

  const downloadAttachment = (attachment: InboxAttachment): void => {
    if (selectedId === null) return;
    setDownloadState({ status: 'requesting', attachmentId: attachment.id });
    downloadInboxAttachment(client, selectedId, attachment, reason, deliverAttachment)
      .then(setDownloadState)
      .catch(() => {
        /* `downloadInboxAttachment` resolves with a `refused` state rather than rejecting. */
      });
  };

  return (
    <div className="wp-stack wp-f">
      <ScreenHeading>{t('nav.inbox')}</ScreenHeading>
      <div className="wp-f-lede">
        <p className="wp-small wp-muted">{t('inbox.intro')}</p>
      </div>

      <div className="wp-filters">
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w13-status-filter">
            {t('inbox.filter.status')}
          </label>
          <select
            className="wp-field__input"
            id="w13-status-filter"
            data-testid="w13-status-filter"
            value={statusFilter}
            onChange={(event) => {
              const next = event.target.value;
              setStatusFilter(next === '' ? '' : (next as InboxParseStatus));
              setSelectedId(null);
            }}
          >
            <option value="">{t('filters.all')}</option>
            {INBOX_PARSE_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(PARSE_STATUS_LABEL[value])}
              </option>
            ))}
          </select>
        </div>
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w13-candidate-filter">
            {t('inbox.filter.candidate')}
          </label>
          <select
            className="wp-field__input"
            id="w13-candidate-filter"
            data-testid="w13-candidate-filter"
            value={candidateFilter}
            onChange={(event) => {
              setCandidateFilter(event.target.value);
              setSelectedId(null);
            }}
          >
            <option value="">{t('filters.all')}</option>
            {candidateOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {list.status === 'loading' ? (
        <Skeleton
          rows={4}
          variant="block"
          label={t('inbox.subjectWord')}
          testId="w13-list-skeleton"
        />
      ) : null}
      {list.status === 'error' && list.error !== null ? (
        <ApiErrorNote
          error={list.error}
          onRetry={list.reload}
          subject={t('inbox.subjectWord')}
          testId="w13-list-error"
        />
      ) : null}

      {list.data === null ? null : rows.length === 0 ? (
        <EmptyState
          title={t('inbox.list.empty.title')}
          description={t('inbox.list.empty.body')}
          testId="w13-list-empty"
        />
      ) : (
        <>
          {hasMore ? (
            <PartialData
              excludes={t('inbox.list.partial.excludes', { shown: rows.length })}
              testId="w13-list-partial"
            />
          ) : null}
          <ul className="wp-rows" data-testid="w13-list">
            {rows.map((message) => (
              <li className="wp-row" key={message.id} data-testid={`w13-msg-${message.id}`}>
                <div className="wp-row__main">
                  <p>
                    <Badge
                      severity={PARSE_STATUS_SEVERITY[message.parse_status]}
                      icon={SEVERITY_ICON[PARSE_STATUS_SEVERITY[message.parse_status]]}
                      label={t(PARSE_STATUS_LABEL[message.parse_status])}
                      testId={`w13-msg-status-${message.id}`}
                    />{' '}
                    <span data-testid={`w13-msg-subject-${message.id}`}>
                      {message.subject ?? t('inbox.list.noSubject')}
                    </span>
                  </p>
                  <p className="wp-small wp-muted" data-testid={`w13-msg-meta-${message.id}`}>
                    {t('inbox.list.meta', {
                      from: message.from_address ?? t('inbox.list.noSender'),
                      when:
                        message.received_at === null
                          ? t('inbox.list.noDate')
                          : formatInstant(message.received_at, locale, timezone),
                    })}
                  </p>
                  <p className="wp-small wp-muted" data-testid={`w13-msg-candidate-${message.id}`}>
                    {message.candidate_id === null
                      ? t('inbox.list.unmatched')
                      : t('inbox.list.matched', {
                          name: candidateName.get(message.candidate_id) ?? message.candidate_id,
                        })}
                  </p>
                  <p
                    className="wp-small wp-muted"
                    data-testid={`w13-msg-attachments-${message.id}`}
                  >
                    {t('inbox.list.attachments', { count: message.attachment_count })}
                  </p>
                </div>
                <div className="wp-row__figure">
                  <button
                    className="wp-button"
                    type="button"
                    data-testid={`w13-open-${message.id}`}
                    onClick={() => {
                      openMessage(message.id);
                    }}
                  >
                    {t('inbox.list.open')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {selectedId === null ? null : (
        <>
          {detail.status === 'loading' ? (
            <Skeleton
              rows={5}
              variant="block"
              label={t('inbox.detail.subject')}
              testId="w13-detail-skeleton"
            />
          ) : null}
          {detail.status === 'error' && detail.error !== null ? (
            <ApiErrorNote
              error={detail.error}
              onRetry={detail.reload}
              subject={t('inbox.detail.subject')}
              testId="w13-detail-error"
            />
          ) : null}

          {current === null ? null : (
            <section
              className="wp-section"
              aria-labelledby="w13-detail-heading"
              data-testid="w13-detail"
            >
              <h2 className="wp-section__title" id="w13-detail-heading" tabIndex={-1}>
                {current.subject ?? t('inbox.list.noSubject')}
              </h2>
              <p>
                <Badge
                  severity={PARSE_STATUS_SEVERITY[current.parse_status]}
                  icon={SEVERITY_ICON[PARSE_STATUS_SEVERITY[current.parse_status]]}
                  label={t(PARSE_STATUS_LABEL[current.parse_status])}
                  testId="w13-detail-status"
                />
              </p>

              <dl className="wp-deflist" data-testid="w13-detail-fields">
                <Field label={t('inbox.detail.from')} value={current.from_address} />
                <Field
                  label={t('inbox.detail.receivedAt')}
                  value={
                    current.received_at === null
                      ? null
                      : formatInstant(current.received_at, locale, timezone)
                  }
                />
              </dl>

              <div className="wp-section__subsection" data-testid="w13-detail-body">
                <h3 className="wp-section__title">{t('inbox.detail.bodyTitle')}</h3>
                <p data-testid="w13-detail-body-text">
                  {current.body_text ?? t('inbox.detail.noBody')}
                </p>
              </div>

              <div className="wp-section__subsection" data-testid="w13-detail-candidate">
                <h3 className="wp-section__title">{t('inbox.detail.candidateTitle')}</h3>
                {current.candidate === null ? (
                  <p data-testid="w13-detail-unmatched">{t('inbox.detail.unmatched')}</p>
                ) : (
                  <p data-testid="w13-detail-matched">
                    <Link to="/recruiting/candidates">{current.candidate.full_name}</Link>
                    {current.candidate.email === null ? null : <> · {current.candidate.email}</>}
                  </p>
                )}
              </div>

              {/* ── The access reason, gating every attachment download below ─────── */}
              <div className="wp-section__subsection" data-testid="w13-access">
                <h4 className="wp-section__title">{t('access.title')}</h4>
                <p>{t('access.why', { subject: t('inbox.subjectAccess') })}</p>
                <p className="wp-small wp-muted">{t('access.recorded')}</p>
                <div className="wp-field">
                  <label className="wp-field__label" htmlFor="w13-reason">
                    {t('access.label')}
                  </label>
                  <input
                    className="wp-field__input"
                    id="w13-reason"
                    data-testid="w13-reason"
                    type="text"
                    value={reason}
                    maxLength={MAX_ACCESS_REASON}
                    autoComplete="off"
                    onChange={(event) => {
                      setReason(event.target.value);
                    }}
                  />
                  <p className="wp-small wp-muted" data-testid="w13-reason-rule">
                    {t('access.rule', { min: MIN_ACCESS_REASON })}
                  </p>
                  {reasonUnencodable ? (
                    <p className="wp-small" role="alert" data-testid="w13-reason-latin1">
                      {t('access.latin1')}
                    </p>
                  ) : null}
                </div>
              </div>

              {/* ── The attachments — stored ones downloadable, rejected ones not ── */}
              <div className="wp-section__subsection" data-testid="w13-attachments">
                <h3 className="wp-section__title">{t('inbox.attachments.title')}</h3>
                {current.attachments.length === 0 ? (
                  <EmptyState
                    title={t('inbox.attachments.empty.title')}
                    testId="w13-attachments-empty"
                  />
                ) : (
                  <ul className="wp-rows" data-testid="w13-attachments-list">
                    {current.attachments.map((attachment) => (
                      <li
                        className="wp-row"
                        key={attachment.id}
                        data-testid={`w13-att-${attachment.id}`}
                      >
                        <div className="wp-row__main">
                          <p>{attachment.filename ?? t('inbox.attachments.unnamed')}</p>
                          <p className="wp-small wp-muted">
                            {t('inbox.attachments.meta', {
                              type: attachment.content_type ?? t('inbox.attachments.typeUnknown'),
                              size: formatSize(attachment.size_bytes, t),
                            })}
                          </p>
                          {attachment.stored ? null : (
                            <p
                              className="wp-small"
                              data-testid={`w13-att-rejected-${attachment.id}`}
                            >
                              {t('inbox.attachments.rejected', {
                                reason:
                                  attachment.rejected_reason ??
                                  t('inbox.attachments.rejectedUnknown'),
                              })}
                            </p>
                          )}
                        </div>
                        {attachment.stored ? (
                          <div className="wp-row__figure">
                            <button
                              className="wp-button"
                              type="button"
                              data-testid={`w13-att-download-${attachment.id}`}
                              disabled={!reasonUsable || downloadState.status === 'requesting'}
                              onClick={() => {
                                downloadAttachment(attachment);
                              }}
                            >
                              {downloadState.status === 'requesting' &&
                              downloadState.attachmentId === attachment.id
                                ? t('inbox.attachments.downloading')
                                : t('inbox.attachments.download')}
                            </button>
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}

                {reasonUsable ? null : (
                  <p className="wp-small wp-muted" data-testid="w13-reason-needed">
                    {t('inbox.attachments.reasonNeeded', { min: MIN_ACCESS_REASON })}
                  </p>
                )}

                {downloadState.status === 'refused' ? (
                  <ApiErrorNote
                    error={downloadState.error}
                    subject={t('inbox.subjectAccess')}
                    testId="w13-download-error"
                  />
                ) : null}
                {downloadState.status === 'delivered' ? (
                  <StatusNote
                    severity="low"
                    title={t('inbox.attachments.delivered', { filename: downloadState.filename })}
                    testId="w13-download-ok"
                  />
                ) : null}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

/** One definition-list row; a null value reads as the em-dash placeholder, never blank. */
function Field({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null;
}): ReactNode {
  const { t } = useI18n();
  return (
    <>
      <dt className="wp-small wp-muted">{label}</dt>
      <dd>{value === null || value === '' ? t('inbox.detail.none') : value}</dd>
    </>
  );
}

/** A file size in KB, or the "unknown" word when the server recorded none. */
function formatSize(bytes: number | null, t: Translate): string {
  if (bytes === null) return t('inbox.attachments.sizeUnknown');
  const kb = Math.max(1, Math.round(bytes / BYTES_PER_KB));
  return t('inbox.attachments.sizeKb', { kb });
}
